const MAX_WIDTH = 1080;
const JPEG_QUALITY = 0.7;

export class PrepareImageUploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PrepareImageUploadError";
  }
}

function isImageFile(file: File): boolean {
  if (file.type.startsWith("image/")) return true;
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return ["jpg", "jpeg", "png", "gif", "webp", "bmp", "heic", "heif"].includes(ext);
}

function canvasHasTransparency(ctx: CanvasRenderingContext2D, width: number, height: number): boolean {
  const { data } = ctx.getImageData(0, 0, width, height);
  for (let i = 3; i < data.length; i += 4) {
    if (data[i]! < 255) return true;
  }
  return false;
}

function canvasToFile(
  canvas: HTMLCanvasElement,
  fileName: string,
  mimeType: "image/jpeg" | "image/png",
  quality?: number,
): Promise<File> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob || blob.size === 0) {
          reject(new PrepareImageUploadError("Não foi possível processar a imagem. Tente outra foto."));
          return;
        }
        const baseName = fileName.replace(/\.[^.]+$/, "") || "foto";
        const ext = mimeType === "image/png" ? "png" : "jpg";
        resolve(new File([blob], `${baseName}.${ext}`, { type: mimeType, lastModified: Date.now() }));
      },
      mimeType,
      quality,
    );
  });
}

async function loadImageBitmap(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file);
  } catch {
    const objectUrl = URL.createObjectURL(file);
    try {
      return await new Promise<ImageBitmap>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          createImageBitmap(img)
            .then(resolve)
            .catch(reject)
            .finally(() => URL.revokeObjectURL(objectUrl));
        };
        img.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          reject(new PrepareImageUploadError("Formato de imagem não suportado. Tente outra foto ou tire uma nova."));
        };
        img.src = objectUrl;
      });
    } catch (error) {
      URL.revokeObjectURL(objectUrl);
      throw error;
    }
  }
}

/**
 * Normaliza imagens antes do upload: redimensiona e comprime.
 * Com transparência (ex.: logos PNG), mantém PNG; caso contrário, JPEG.
 * Arquivos não-imagem são devolvidos sem alteração.
 */
export async function prepareImageForUpload(file: File): Promise<File> {
  if (!file || file.size === 0) {
    throw new PrepareImageUploadError("O arquivo selecionado está vazio. Selecione outra imagem.");
  }

  if (!isImageFile(file)) {
    return file;
  }

  const bitmap = await loadImageBitmap(file);
  try {
    const scale = bitmap.width > MAX_WIDTH ? MAX_WIDTH / bitmap.width : 1;
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
      throw new PrepareImageUploadError("Não foi possível processar a imagem. Tente outra foto.");
    }
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0, width, height);

    if (canvasHasTransparency(ctx, width, height)) {
      return await canvasToFile(canvas, file.name, "image/png");
    }
    return await canvasToFile(canvas, file.name, "image/jpeg", JPEG_QUALITY);
  } finally {
    bitmap.close();
  }
}

export async function prepareImageFilesForUpload(
  files: Partial<Record<string, File | undefined>>,
): Promise<Partial<Record<string, File | undefined>>> {
  const result: Partial<Record<string, File | undefined>> = { ...files };
  for (const [key, file] of Object.entries(files)) {
    if (file instanceof File) {
      result[key] = await prepareImageForUpload(file);
    }
  }
  return result;
}
